import { creatorbase, youtube } from '@round/api';
import { Skeleton, SkeletonTableCell, TableProps, getTableMetaHelper } from '@round/ui-kit';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import VirtualizedPostsTable from 'Modules/Plans/Posts/components/PostsTable/VirtualizedPostsTable';
import postsTableStyles from 'Modules/Plans/Posts/components/PostsTable/PostsTable.module.css';
import { usePublicReportYoutubePosts } from './hooks/usePublicReportYoutubePosts';
import { CampaignTableRow } from '../../Campaigns/CampaignsTable/types';
import PostsTableStatusCell, {
    StatusData,
} from 'Modules/Plans/Posts/components/PostsTable/cells/StatusCell/PostsTableStatusCell';
import { BasePostTableMeta } from 'Modules/Plans/Posts/types';
import PostStatsValueCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsStatsValueCell';
import PostStatsFooterTotalCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsTableStatFooterCell';
import PostStatsViewsFooterCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostStatsViewsFooterCell';
import PostsTableUploadedDateCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsTableUploadedDateCell';
import PublicPostsTableAccountCell, {
    AccountData,
} from 'Modules/Plans/Posts/components/PostsTable/cells/PublicPostsTableAccountCell/PublicPostsTableAccountCell';
import { getYoutubeEngagementRatePercentageString, getYoutubeTotalEngagement } from 'Modules/Plans/Posts/helpers';
import PostsTableEngagementRateFooterCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsTableEngagementRateFooterCell';
import PostsTableEngagementsFooterCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsTableEngagementsFooterCell';
import { TooltipHeader } from '@round/whitelabel-ui';
import { tooltipCopy } from 'Modules/Plans/helpers';

export type YoutubePostsTableRow = creatorbase.PublicYoutubePost & {
    channel: youtube.Channel | null;
};

type Props = Pick<TableProps<YoutubePostsTableRow>, 'noDataLabel'> & {
    campaign: CampaignTableRow;
};

type TableCellContext<TKey extends keyof YoutubePostsTableRow> = CellContext<
    YoutubePostsTableRow,
    YoutubePostsTableRow[TKey]
>;

type Meta = BasePostTableMeta<YoutubePostsTableRow> & {
    campaign: CampaignTableRow;
};

const getTableMeta = getTableMetaHelper<Meta>();

const PublicReportYoutubePostsTable = ({ campaign }: Props) => {
    const {
        posts,
        isPostsLoading,
        isPostsError,
        channelsData,
        getIsAccountDataLoading,
        postsTotals,
        areTotalsLoading,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
    } = usePublicReportYoutubePosts(campaign.id);

    const columns = useMemo<ColumnDef<YoutubePostsTableRow, any>[]>(
        () => [
            {
                header: 'ID',
                meta: { className: postsTableStyles.idColumn },
                accessorKey: 'id',
                cell: ({ getValue, table }: TableCellContext<'id'>) => {
                    const { isLoading } = getTableMeta(table);

                    if (isLoading) {
                        return <Skeleton />;
                    }

                    return getValue().slice(-8);
                },
            },
            {
                header: 'Account',
                meta: { className: postsTableStyles.accountColumn },
                accessorFn: (row): AccountData => ({
                    image: row.channel?.thumbnail || undefined,
                    title: row.channel?.title ?? '-',
                }),
                id: 'account',
                cell: PublicPostsTableAccountCell,
            },
            {
                header: 'Status',
                meta: { className: postsTableStyles.statusColumn },
                id: 'status',
                accessorFn: (original): StatusData => ({
                    postUrl: original.post_url,
                    platform: original.platform,
                    isReadOnly: true,
                }),
                cell: PostsTableStatusCell,
            },
            {
                header: 'Uploaded date',
                meta: { className: postsTableStyles.uploadedDateColumn },
                id: 'uploadedDate',
                cell: PostsTableUploadedDateCell,
            },
            {
                header: 'Views',
                meta: { className: postsTableStyles.statColumn },
                id: 'views',
                accessorFn: (original) => original.view_count,
                cell: PostStatsValueCell,
                footer: PostStatsViewsFooterCell,
            },
            {
                header: 'Total engagements',
                meta: { className: postsTableStyles.totalEngagementsColumn },
                id: 'totalEngagements',
                accessorFn: (row) => getYoutubeTotalEngagement(row),
                cell: PostStatsValueCell,
                footer: PostsTableEngagementsFooterCell,
            },
            {
                header: () => <TooltipHeader header="ER" tooltipBody={tooltipCopy.engagementRate} />,
                meta: { className: postsTableStyles.statColumn },
                id: 'engagementRate',
                accessorFn: (row) => getYoutubeEngagementRatePercentageString(row),
                cell: SkeletonTableCell,
                footer: PostsTableEngagementRateFooterCell,
            },
            {
                header: 'Likes',
                meta: { className: postsTableStyles.statColumn },
                accessorFn: (original) => original.youtube_details?.like_count,
                cell: PostStatsValueCell,
                footer: (context) => <PostStatsFooterTotalCell {...context} totalsField="like_count" />,
            },
            {
                header: 'Comments',
                meta: { className: postsTableStyles.statColumn },
                accessorFn: (original) => original.youtube_details?.comment_count,
                id: 'comments',
                cell: PostStatsValueCell,
                footer: (context) => <PostStatsFooterTotalCell {...context} totalsField="comment_count" />,
            },
        ],
        []
    );

    const meta: Meta = {
        isLoading: isPostsLoading,
        campaign,
        getIsAccountDataLoading,
        managedBy: campaign.managed_by,
        areApiTotalsEnabled: true,
        postsTotals,
        areTotalsLoading,
    };

    const rows: YoutubePostsTableRow[] = (posts as creatorbase.PublicPost[])
        .filter((p): p is creatorbase.PublicYoutubePost => p.platform === 'youtube')
        .map((post) => {
            const channelId = post.youtube_details?.account_id;
            const channel = channelId ? channelsData?.get(channelId) ?? null : null;

            return {
                ...post,
                channel,
            };
        });

    return (
        <VirtualizedPostsTable
            columns={columns}
            data={rows}
            meta={meta}
            isLoading={isPostsLoading}
            hasError={isPostsError}
            onEndReached={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
        />
    );
};

export default PublicReportYoutubePostsTable;
